import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  inject,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faTrashCan, faUpload } from '@fortawesome/pro-solid-svg-icons';
import { BannerService } from '@terminus-ui/banner';
import { ModalService } from '@terminus-ui/modal';
import {
  PageHeaderButtonsComponent,
  PageHeaderComponent,
} from '@terminus-ui/page-header';
import { AuthService } from 'auth';
import { AssetType, CreativeAssetLibraryApi } from 'creative-asset-library-api';
import {
  ActionDefinition,
  DataContextService,
  filters,
  isFullDataContext,
  LakeFrontV2Module,
  PrimeTableComponent,
  ReportFilterService,
  ValueExpression,
} from 'lake-front-v2';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ToastModule } from 'primeng/toast';
import { combineLatest, filter, map, of, take } from 'rxjs';
import { CreativeUploaderComponent } from '../creative-uploader/creative-uploader.component';
import { ArchiveConfirmationModal } from './archive-confirmation.modal';

type LibraryAsset = {
  asset: {
    current_value: {
      attributes: {
        asset_type: AssetType;
        asset_type_display: 'Image' | 'Video' | 'HTML5';
        dimensions: string;
        height_px: number;
        size_in_bytes: number;
        source_url: string;
        width_px: number;
      };
      data_extensions: any[];
      display_name: string;
      entity_filters: {
        cal_asset_id: string;
      };
      entity_id: {
        asset_id: string;
      };
      entity_type: 'asset';
    };
  };
  uploaded_date_utc: {
    current_value: {
      day: number;
      month: number;
      year: number;
    };
  };
  dimensions: {
    current_value: string;
  };
};
@Component({
  selector: 'lib-creative-library',
  imports: [
    CommonModule,
    ButtonModule,
    FontAwesomeModule,
    PageHeaderComponent,
    LakeFrontV2Module,
    CreativeUploaderComponent,
    PageHeaderButtonsComponent,
    ToastModule,
  ],
  providers: [MessageService],
  templateUrl: './creative-library.component.html',
  styleUrl: './creative-library.component.scss',
})
export class CreativeLibraryComponent implements OnDestroy {
  @ViewChild('creativeUploader') creativeUploader: CreativeUploaderComponent;
  @ViewChild('audioBannerFileInput') audioBannerFileInput: ElementRef;

  private messageService = inject(MessageService);
  private activatedRoute = inject(ActivatedRoute);

  faUpload = faUpload;

  private dcs = inject(DataContextService);
  private reportFilterManager = inject(ReportFilterService);
  private authService = inject(AuthService);

  private creativeAssetLibraryApi = inject(CreativeAssetLibraryApi);
  private bannerService = inject(BannerService);
  private modalService = inject(ModalService);

  public showUploader = false;
  public selectedAudioAssetId: string;

  public rowActions: ActionDefinition[] = [
    {
      callback: (item) => {
        this.modalService
          .openDialog({
            affirmButton: {
              text: 'Archive',
              isDanger: true,
              shouldClose: true,
              callbackFn: () => {
                this.creativeAssetLibraryApi
                  .setAssetLibraryVisibility({
                    assetId: item.asset.current_value.entity_id.asset_id,
                    visibleInLibrary: false,
                  })
                  .pipe(take(1))
                  .subscribe(
                    () => {
                      this.bannerService.pushBanner({
                        isClosable: true,
                        isFloating: true,
                        title: '',
                        subtitle:
                          'Creative is now archiving. A new report will be available shortly.',
                        type: 'success',
                      });
                      // refresh the table
                      this.refreshInterval.next(1000);
                    },
                    (error) => {
                      this.bannerService.pushBanner({
                        isClosable: true,
                        isFloating: true,
                        title: '',
                        subtitle: `Fail to archive Creative: ${error.message}`,
                        type: 'error',
                      });
                    },
                  );
              },
            },
            cancelButton: {
              text: 'Cancel',
              shouldClose: true,
            },
            title: 'Archive Creative',
            content: ArchiveConfirmationModal,
          })
          .pipe(take(1))
          .subscribe();
      },
      description: `Archive Creative: this action cannot be undone. This won't affect running ads.`,
      label: 'Archive',
      id: 'entity_id',
      icon: faTrashCan,
      severity: 'danger',
    },
    {
      callback: (item) => {
        this.selectedAudioAssetId =
          item['asset']?.current_value?.entity_id?.asset_id;
        this.audioBannerFileInput.nativeElement.click();
      },
      description: 'Upload Companion Banner',
      label: 'Upload Banner',
      id: 'upload',
      icon: faUpload,
      hidden: (item) => {
        if (item['asset_type_display']?.current_value !== 'Audio') {
          return true;
        } else if (
          item['asset_status_composite']?.current_value
            ?.calculated_status_message !== 'Pending'
        ) {
          return true;
        } else {
          return false;
        }
      },
    },
  ];

  @ViewChild('primeTable') primeTable: PrimeTableComponent;

  @Output() selectedAssets: EventEmitter<LibraryAsset[]> = new EventEmitter<
    LibraryAsset[]
  >();

  tabIndexMap = {
    IMAGE: 0,
    CTV: 1,
    VIDEO: 2,
    AUDIO: 3,
  };

  openingTabIndex: number = 0;
  creativesUploaded: boolean = false;

  constructor() {
    const tab = this.activatedRoute.snapshot.paramMap.get('tab');
    if (this.activatedRoute.snapshot.paramMap.get('tab')) {
      this.openingTabIndex = this.tabIndexMap[tab || 0];
      this.showUploader = true;
    }

    this.dataContext
      .pipe(filter(isFullDataContext), takeUntilDestroyed())
      .subscribe(() => this.refreshInterval.next(null));
  }

  resetTableSelections() {
    this.primeTable.selectedRows = [];
    this.selectedAssets.emit([]);
  }

  handleUploadModalClose() {
    if (this.creativesUploaded) {
      // start polling for new data
      this.refreshInterval.next(1000);
      // inform the user that the library is updating
      if (this.creativesUploaded) {
        this.messageService.add({
          key: 'library-toast',
          severity: 'info',
          detail:
            'Newly uploaded creatives may not be immediately visible in the library. \n We will notify you shortly when your creative is available.',
          life: 5000,
        });
      }
    }
  }

  userInputForReport$ = combineLatest({
    marketer_uuid: this.authService.myTerminusMarketer$.pipe(
      map((u) => u.uuid),
    ),
  });

  private dcContext = this.dcs.controllableNewDataContext(
    'creative_asset_library.assets.v1',
    of({}),
    this.userInputForReport$,
    this.authService.myTerminusToken$,
    {
      newReportMessage:
        'Your recently added creatives are ready. Show them in the library?',
    },
  );

  public dataContext = this.dcContext[0];
  public refreshInterval = this.dcContext[1].refreshInterval;

  tableFilter = (filterValue: string | null): ValueExpression | null => {
    if (filterValue === null || filterValue === '') return null;

    return {
      function: {
        function: 'CONTAINS',
        parameters: [
          { literal: { alphaNumeric: filterValue } },
          { rowValue: { metadataId: 'file_name' } },
        ],
      },
    };
  };

  defaultFilters = [
    {
      fieldName: 'asset_type_display',
      filterDefinition: filters.StringEqualsAnyFilterDefinition,
      removable: false,
      defaultFilterLocationIds: ['mini-filters'],
    },
    {
      fieldName: 'dimensions',
      filterDefinition: filters.StringEqualsAnyFilterDefinition,
      removable: false,
      defaultFilterLocationIds: ['mini-filters'],
    },
  ];

  filterManager = this.reportFilterManager.newFilterManager({
    dataContext: this.dataContext,
    reportName: 'assets',
    defaultFilters: this.defaultFilters,
    unsubscribeFunction: takeUntilDestroyed(),
  });

  ngOnDestroy(): void {
    this.refreshInterval.next(null);
  }
}
